import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  Users,
  Upload,
  Plus,
  Trash2,
  CheckCircle2,
  Clock,
  FileUp,
  AlertTriangle,
  Loader2,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useToast } from '../components/Toast';
import DepartmentSelect from '../components/DepartmentSelect';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fadeUp = { hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } };
const EMPTY_FORM = {
  employee_id: '',
  full_name: '',
  work_email: '',
  department: '',
  age: 30,
  gender: 'female',
  designation: 'professional',
};

export default function Teams() {
  const { show, node } = useToast();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showImport, setShowImport] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [importErrors, setImportErrors] = useState([]);
  const [manualForm, setManualForm] = useState(EMPTY_FORM);

  const fetchData = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/employees`, { withCredentials: true });
      setEmployees(data.employees || []);
    } catch {
      /* ignore */
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    setImportErrors([]);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const { data } = await axios.post(`${API}/employees/import`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (data.errors && data.errors.length > 0) {
        setImportErrors(data.errors);
        show(`${data.errors.length} validation errors found. Fix and re-upload.`, 'error');
      } else {
        show(`${data.imported} employees imported${data.skipped > 0 ? `, ${data.skipped} skipped` : ''}`);
        fetchData();
        setShowImport(false);
      }
    } catch (err) {
      show(err.response?.data?.detail || 'Import failed', 'error');
    }
    setUploading(false);
  };

  const handleManualAdd = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/employees`, manualForm, { withCredentials: true });
      show('Employee added successfully');
      setManualForm(EMPTY_FORM);
      setShowManual(false);
      fetchData();
    } catch (err) {
      show(err.response?.data?.detail || 'Failed to add employee', 'error');
    }
  };

  const handleDelete = async (email) => {
    try {
      await axios.delete(`${API}/employees/${email}`, { withCredentials: true });
      show('Employee removed');
      fetchData();
    } catch {
      show('Failed to delete', 'error');
    }
  };

  const deptMap = {};
  employees.forEach((e) => {
    const d = e.department || 'Unassigned';
    (deptMap[d] = deptMap[d] || []).push(e);
  });
  const departments = Object.entries(deptMap);

  const StatusBadge = ({ status }) => (
    <span
      className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full ${
        status === 'completed'
          ? 'bg-green-50 text-green-700'
          : status === 'invited'
          ? 'bg-amber-50 text-amber-700'
          : 'bg-slate-100 text-slate-500'
      }`}
    >
      {status === 'completed' && <CheckCircle2 className="w-3 h-3" />}
      {status === 'invited' && <Clock className="w-3 h-3" />}
      {status}
    </span>
  );

  return (
    <div className="p-6 md:p-8 lg:p-10 max-w-7xl mx-auto" data-testid="teams-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl font-semibold text-slate-800">Teams</h1>
          <p className="text-sm text-slate-500 mt-1">
            {employees.length} employees across {departments.length} teams
          </p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={() => setShowImport(true)} className="border-slate-300" data-testid="import-btn">
            <Upload className="w-4 h-4 mr-2" /> Import
          </Button>
          <Button onClick={() => setShowManual(true)} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="add-employee-btn">
            <Plus className="w-4 h-4 mr-2" /> Add Employee
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
        </div>
      ) : departments.length === 0 ? (
        <div className="glass-card rounded-2xl p-12 text-center">
          <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 mb-4">No employees yet. Import a CSV/XLSX or add them manually.</p>
          <Button onClick={() => setShowImport(true)} className="bg-teal-600 hover:bg-teal-700 text-white">
            <Upload className="w-4 h-4 mr-2" /> Import Workforce
          </Button>
        </div>
      ) : (
        <div className="space-y-6">
          {departments.map(([dept, members]) => (
            <motion.div
              key={dept}
              initial="hidden"
              animate="visible"
              variants={fadeUp}
              className="bg-white rounded-2xl border border-slate-200 overflow-hidden"
            >
              <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100 bg-slate-50">
                <h3 className="font-heading font-medium text-slate-800">{dept}</h3>
                <span className="text-xs text-slate-400">{members.length} members</span>
              </div>
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-100">
                    <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-5 py-2.5">Name</th>
                    <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-5 py-2.5">Email</th>
                    <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-5 py-2.5">Role</th>
                    <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-5 py-2.5">Status</th>
                    <th className="text-right text-xs font-semibold text-slate-500 uppercase tracking-wider px-5 py-2.5">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {members.map((emp, i) => (
                    <tr key={i} className="border-b border-slate-50 hover:bg-slate-25">
                      <td className="px-5 py-3 text-sm font-medium text-slate-800">{emp.full_name}</td>
                      <td className="px-5 py-3 text-sm text-slate-500">{emp.work_email}</td>
                      <td className="px-5 py-3 text-sm text-slate-600 capitalize">{emp.designation}</td>
                      <td className="px-5 py-3"><StatusBadge status={emp.review_status} /></td>
                      <td className="px-5 py-3 text-right">
                        <button
                          onClick={() => handleDelete(emp.work_email)}
                          className="text-slate-400 hover:text-red-500 transition-colors"
                          data-testid={`delete-emp-${emp.work_email}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </motion.div>
          ))}
        </div>
      )}

      {/* Import modal */}
      {showImport && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" data-testid="import-modal">
          <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-white rounded-2xl p-8 w-full max-w-lg mx-4 shadow-2xl max-h-[80vh] overflow-y-auto">
            <h3 className="font-heading text-xl font-medium text-slate-800 mb-4">Import Workforce</h3>
            <p className="text-sm text-slate-500 mb-6">
              Upload a CSV or XLSX with columns: Employee_ID, Full_Name, Work_Email, Department. Optional: Age, Gender, Designation.
            </p>
            <label className="flex flex-col items-center justify-center border-2 border-dashed border-slate-200 rounded-xl p-8 cursor-pointer hover:border-teal-400 transition-colors">
              <FileUp className="w-10 h-10 text-slate-300 mb-3" />
              <span className="text-sm text-slate-500">{uploading ? 'Uploading...' : 'Click to upload CSV or XLSX'}</span>
              <input type="file" accept=".csv,.xlsx" onChange={handleFileUpload} className="hidden" data-testid="file-upload-input" />
            </label>
            {importErrors.length > 0 && (
              <div className="mt-4 p-4 rounded-xl bg-red-50 border border-red-100 max-h-48 overflow-y-auto">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="w-4 h-4 text-red-500" />
                  <span className="text-sm font-medium text-red-700">{importErrors.length} Validation Errors</span>
                </div>
                <div className="space-y-1">
                  {importErrors.slice(0, 20).map((err, i) => (
                    <p key={i} className="text-xs text-red-600">
                      Row {err.row}: <strong>{err.field}</strong> — {err.error}
                    </p>
                  ))}
                </div>
              </div>
            )}
            <div className="flex items-center gap-4 mt-6">
              <Button variant="outline" onClick={() => { setShowImport(false); setShowManual(true); setImportErrors([]); }} className="flex-1">
                <Plus className="w-4 h-4 mr-2" /> Manual Entry
              </Button>
              <Button variant="ghost" onClick={() => { setShowImport(false); setImportErrors([]); }}>Cancel</Button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Manual add modal */}
      {showManual && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" data-testid="manual-modal">
          <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-white rounded-2xl p-8 w-full max-w-lg mx-4 shadow-2xl">
            <h3 className="font-heading text-xl font-medium text-slate-800 mb-4">Add Employee</h3>
            <form onSubmit={handleManualAdd} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <Input placeholder="Employee ID" value={manualForm.employee_id} onChange={(e) => setManualForm({ ...manualForm, employee_id: e.target.value })} required />
                <Input placeholder="Full Name" value={manualForm.full_name} onChange={(e) => setManualForm({ ...manualForm, full_name: e.target.value })} required />
              </div>
              <Input type="email" placeholder="Work Email" value={manualForm.work_email} onChange={(e) => setManualForm({ ...manualForm, work_email: e.target.value })} required />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <DepartmentSelect
                  id="manual-department"
                  label=""
                  value={manualForm.department}
                  onChange={(department) => setManualForm({ ...manualForm, department })}
                  required
                  data-testid="manual-department"
                />
                <Input type="number" placeholder="Age" value={manualForm.age} onChange={(e) => setManualForm({ ...manualForm, age: parseInt(e.target.value) || 30 })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <select value={manualForm.gender} onChange={(e) => setManualForm({ ...manualForm, gender: e.target.value })} className="border border-slate-200 rounded-md px-3 py-2 text-sm">
                  <option value="female">Female</option>
                  <option value="male">Male</option>
                </select>
                <select value={manualForm.designation} onChange={(e) => setManualForm({ ...manualForm, designation: e.target.value })} className="border border-slate-200 rounded-md px-3 py-2 text-sm">
                  <option value="professional">Professional</option>
                  <option value="support">Support</option>
                </select>
              </div>
              <div className="flex gap-3 pt-2">
                <Button type="submit" className="flex-1 bg-teal-600 hover:bg-teal-700 text-white">Add Employee</Button>
                <Button type="button" variant="ghost" onClick={() => setShowManual(false)}>Cancel</Button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
      {node}
    </div>
  );
}
